import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import BloqueFicha from "../components/funcionales/BloqueFicha";
import {
  borrarAutomatizacion,
  corridasDe,
  corridasPublicasDe,
  crearAutomatizacion,
  genteParaCompartir,
  guardarAutomatizacion,
  hojasDePlanilla,
  miMail,
  misPlanillas,
  pedirPrueba,
  prenderApagar,
  seguirCorrida,
  traerAutomatizacion,
} from "../services/automatizaciones";
import {
  AYUDA_VARIABLE,
  CANALES_PLANILLA,
  DIAS,
  DISPARADORES,
  PASOS,
  TERMINADAS,
  VARIABLES_DISPARADOR,
  detalleLegible,
  disparadorVacio,
  estadoCorrida,
  formAJson,
  formVacio,
  grupoDe,
  insertarVariable,
  jsonAForm,
  mapaDeGente,
  ordenarPasos,
  pasoVacio,
  resumenDetalle,
  validar,
  variablesDisponibles,
} from "../shared/automatizaciones";
import { diaHoraAr } from "../shared/fechaAr";

import "./css/fichas-pantalla.css";
import "./css/automatizaciones.css";

/* Una automatización: el editor de bloques, la prueba y el historial.
 *
 * ── BLOQUES APILADOS, Y NO UN LIENZO ─────────────────────────────────────
 *
 * Igna, 03/10/2026: «estilo programación por bloques». Un lienzo con flechas
 * (Zapier, Node-RED) deja armar cualquier cosa y entender poco; acá los flujos
 * son siempre la misma frase —«Cuando… tomá… hacé…»— así que los bloques van
 * uno abajo del otro, cada tipo con su color, y se leen de arriba abajo como
 * se dicen. Lo que no entra en esa frase no se puede armar, y está bien: es un
 * menú cerrado a propósito (ver el contrato en la migración).
 *
 *   Cuando…  uno, obligatorio: a una hora, llega un mail, cambia una planilla
 *   Tomá…    opcionales: leer el mail con IA, leer una celda → una variable
 *   Hacé…    al menos uno: escribir una celda, WhatsApp, mail, mandar una
 *            planilla entera (FiboSheets #9: «mandame Costos los viernes a
 *            las 9»: «A una hora» + este bloque)
 *
 * Las variables se ofrecen como chips en cada bloque, SÓLO las que existen en
 * ese punto (las del disparador más lo que guardaron los «Tomá» anteriores).
 * Escribir «{{precio}}» a mano anda igual, pero el chip evita la errata que
 * haría llegar «{{preico}}» tal cual al WhatsApp de alguien — y por eso
 * también se valida al guardar.
 *
 * ── NACE APAGADA, SE PRUEBA, SE PRENDE ───────────────────────────────────
 *
 * «Probar» le pide al bot una corrida que NO escribe ni manda nada y deja
 * dicho lo que HABRÍA hecho. Se prueba lo GUARDADO: con cambios sin guardar,
 * la prueba diría algo de una versión que no es la que está en pantalla.
 *
 * ── DE OTRO, SE MIRA ──────────────────────────────────────────────────────
 *
 * La de otro se abre igual, con todo deshabilitado y sin botones: se puede
 * ver cómo está armada y su historial, que es la mitad de «que todos puedan
 * verlo».
 *
 * Del historial ajeno se ve CUÁNDO corrió y CÓMO le fue, no QUÉ leyó ni QUÉ
 * mandó. El `detalle` de una corrida trae datos —los valores del rango que
 * mira, «B3: 100 → 120», lo que la IA sacó de un mail, el texto del
 * WhatsApp— y un 🔔 sobre una planilla privada los dejaba a la vista de todo
 * el hub. La base ya no se lo da a nadie más que al dueño
 * (`20261005060000_automatizaciones_valores_privados.sql`), y la pantalla lo
 * dice en vez de dibujar una columna vacía que parezca una falla.
 */

const ESPERA_BOT_MS = 120000;

/* ── Piezas chicas ─────────────────────────────────────────────────────── */

function Chips({ variables, onElegir, deshabilitado }) {
  if (!variables.length) {
    return <p className="auto-chips-vacio tenue">Todavía no hay variables para usar en este bloque.</p>;
  }
  return (
    <div className="auto-chips" aria-label="Variables para insertar">
      <span className="tenue">Insertar:</span>
      {variables.map((v) => (
        <button
          key={v} type="button" className="auto-chip" disabled={deshabilitado}
          title={AYUDA_VARIABLE[v] || "Lo que guardó un bloque anterior"}
          // El mousedown no se lleva el foco: el cursor queda donde estaba.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onElegir(v)}
        >
          {`{{${v}}}`}
        </button>
      ))}
    </div>
  );
}

function Errores({ lista }) {
  if (!lista.length) return null;
  return (
    <ul className="auto-errores" role="alert">
      {lista.map((e, i) => <li key={i}>{e.mensaje}</li>)}
    </ul>
  );
}

function Campo({ label, children, ayuda }) {
  return (
    <label className="auto-campo">
      <span className="auto-campo-label">{label}</span>
      {children}
      {ayuda && <span className="auto-campo-ayuda tenue">{ayuda}</span>}
    </label>
  );
}

/* Elegir planilla (y hoja, si tiene varias). `opciones` son las planillas que
   se pueden elegir acá: para mirar y escribir, sólo las mías; para leer,
   también las compartidas conmigo. Una que no está en la lista (la de otro,
   vista en sólo lectura) se muestra igual, sin nombre. */
function ElegirPlanilla({ planillaId, hoja, opciones, hojas, onCambio, deshabilitado, cargarHojas, etiqueta, sinHoja = false }) {
  useEffect(() => { if (planillaId && !sinHoja) cargarHojas(planillaId); }, [planillaId, cargarHojas, sinHoja]);
  const nombres = planillaId && !sinHoja ? hojas[planillaId] : null;
  const conocida = opciones.some((p) => p.id === planillaId);
  return (
    <>
      <Campo label={etiqueta}>
        <select
          value={planillaId || ""} disabled={deshabilitado}
          onChange={(e) => onCambio({ planilla_id: e.target.value, hoja: null })}
        >
          <option value="">Elegí una planilla…</option>
          {planillaId && !conocida && <option value={planillaId}>Una planilla que no podés abrir</option>}
          {opciones.map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.ajena ? ` (de ${p.ajena})` : ""}</option>)}
        </select>
      </Campo>
      {nombres && nombres.length > 1 && (
        <Campo label="Hoja">
          <select
            value={hoja || ""} disabled={deshabilitado}
            onChange={(e) => onCambio({ hoja: e.target.value || null })}
          >
            <option value="">{`La primera (${nombres[0]})`}</option>
            {nombres.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </Campo>
      )}
    </>
  );
}

/* A quién: «Yo» o gente del hub. Nunca un número suelto (ver el contrato:
   un bloque que manda a cualquiera es una máquina de spam con la firma de la
   empresa). */
function Destinatarios({ a, gente, nombres, onCambio, deshabilitado, yo }) {
  const lista = Array.isArray(a) ? a : [];
  const sacar = (m) => onCambio(lista.filter((x) => x !== m));
  const sumar = (m) => { if (m && !lista.includes(m)) onCambio([...lista, m]); };
  const quedan = gente.filter((g) => g.mail !== yo && !lista.includes(g.mail));
  return (
    <div className="auto-campo">
      <span className="auto-campo-label">A quién</span>
      <div className="auto-destinos">
        {lista.map((m) => (
          <span key={m} className="auto-destino">
            {m === "yo" ? "Yo" : (nombres[m] || m)}
            {!deshabilitado && (
              <button type="button" aria-label={`Sacar a ${m === "yo" ? "mí" : (nombres[m] || m)}`} onClick={() => sacar(m)}>×</button>
            )}
          </span>
        ))}
        {!deshabilitado && !lista.includes("yo") && (
          <button type="button" className="tk-chip" onClick={() => sumar("yo")}>+ Yo</button>
        )}
        {!deshabilitado && (
          <select aria-label="Sumar a alguien" value="" onChange={(e) => sumar(e.target.value)}>
            <option value="">Sumar a alguien del hub…</option>
            {quedan.map((g) => <option key={g.mail} value={g.mail}>{g.nombre}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}

/* ── Un bloque de paso ─────────────────────────────────────────────────── */

const CAMPO_PRINCIPAL = { escribir_celda: "valor", whatsapp: "texto", mail: "texto" };

function BloquePaso({
  paso, indice, total, variables, onCambio, onMover, onSacar, ro, ctx, errores,
}) {
  const refs = useRef({});
  const ultimo = useRef(CAMPO_PRINCIPAL[paso.tipo] || null);
  const tocado = useRef(false); // sin tocar ningún campo, el chip va al final
  const grupo = grupoDe(paso.tipo);
  const set = (campos) => onCambio({ ...paso, ...campos });

  /* El chip va donde estaba el cursor, en el último campo de texto tocado de
     este bloque (o en el principal, si no se tocó ninguno). */
  const insertar = (nombre) => {
    const campo = ultimo.current;
    if (!campo) return;
    const el = refs.current[campo];
    const conCursor = el && tocado.current;
    const { texto, cursor } = insertarVariable(paso[campo], nombre, conCursor ? el.selectionStart : undefined, conCursor ? el.selectionEnd : undefined);
    set({ [campo]: texto });
    if (el) {
      setTimeout(() => {
        el.focus();
        if (el.setSelectionRange) el.setSelectionRange(cursor, cursor);
      }, 0);
    }
  };
  const conVariables = (campo) => ({
    ref: (el) => { refs.current[campo] = el; },
    onFocus: () => { ultimo.current = campo; tocado.current = true; },
    value: paso[campo] ?? "",
    onChange: (e) => set({ [campo]: e.target.value }),
    disabled: ro,
  });
  const llevaChips = !!CAMPO_PRINCIPAL[paso.tipo];

  return (
    <section className={`auto-bloque auto-bloque--${grupo}`} aria-label={`${grupo === "toma" ? "Tomá" : "Hacé"}: ${PASOS[paso.tipo].label}`}>
      <div className="auto-bloque-cab">
        <span className="auto-bloque-verbo">{grupo === "toma" ? "Tomá…" : "Hacé…"}</span>
        <span className="auto-bloque-tipo">{PASOS[paso.tipo].label}</span>
        {!ro && (
          <span className="auto-bloque-mover">
            <button type="button" aria-label="Subir" disabled={indice === 0} onClick={() => onMover(-1)}>↑</button>
            <button type="button" aria-label="Bajar" disabled={indice === total - 1} onClick={() => onMover(1)}>↓</button>
            <button type="button" aria-label="Sacar este bloque" onClick={onSacar}>✕</button>
          </span>
        )}
      </div>
      <div className="auto-bloque-cuerpo">
        {paso.tipo === "leer_mail_ia" && (
          <>
            <Campo label="Qué dato sacar del mail" ayuda="Contalo como se lo pedirías a alguien: «el precio del gasoil común», «el total de la factura».">
              <textarea rows={2} value={paso.que} disabled={ro} onChange={(e) => set({ que: e.target.value })} />
            </Campo>
            <Campo label="Guardar como" ayuda="El nombre de la variable, sin espacios. Después se usa como {{nombre}}.">
              <input value={paso.guardar_como} disabled={ro} placeholder="precio" onChange={(e) => set({ guardar_como: e.target.value })} />
            </Campo>
          </>
        )}
        {paso.tipo === "leer_celda" && (
          <>
            <ElegirPlanilla
              etiqueta="De qué planilla" planillaId={paso.planilla_id} hoja={paso.hoja}
              opciones={ctx.planillas} hojas={ctx.hojas} cargarHojas={ctx.cargarHojas}
              onCambio={set} deshabilitado={ro}
            />
            <div className="auto-fila">
              <Campo label="Celda"><input value={paso.celda} disabled={ro} placeholder="B3" onChange={(e) => set({ celda: e.target.value })} /></Campo>
              <Campo label="Guardar como"><input value={paso.guardar_como} disabled={ro} placeholder="valor" onChange={(e) => set({ guardar_como: e.target.value })} /></Campo>
            </div>
          </>
        )}
        {paso.tipo === "escribir_celda" && (
          <>
            <ElegirPlanilla
              etiqueta="En cuál de tus planillas" planillaId={paso.planilla_id} hoja={paso.hoja}
              opciones={ro ? ctx.planillas : ctx.planillasMias} hojas={ctx.hojas} cargarHojas={ctx.cargarHojas}
              onCambio={set} deshabilitado={ro}
            />
            <div className="auto-fila">
              <Campo label="Celda"><input value={paso.celda} disabled={ro} placeholder="B3" onChange={(e) => set({ celda: e.target.value })} /></Campo>
              <Campo label="Qué escribir"><input aria-label="Qué escribir" placeholder="{{precio}}" {...conVariables("valor")} /></Campo>
            </div>
          </>
        )}
        {paso.tipo === "mandar_planilla" && (
          <>
            {/* Va el libro entero (todas las hojas): no se elige hoja. */}
            <ElegirPlanilla
              etiqueta="Qué planilla" planillaId={paso.planilla_id} sinHoja
              opciones={ctx.planillasConDueno} hojas={ctx.hojas} cargarHojas={ctx.cargarHojas}
              onCambio={(c) => set({ planilla_id: c.planilla_id })} deshabilitado={ro}
            />
            <div className="auto-campo">
              <span className="auto-campo-label">Cómo</span>
              <div className="auto-dias" role="group" aria-label="Cómo mandarla">
                {CANALES_PLANILLA.map(({ canal, label }) => {
                  const por = Array.isArray(paso.por) ? paso.por : [];
                  const activo = por.includes(canal);
                  return (
                    <button
                      key={canal} type="button" disabled={ro} aria-pressed={activo}
                      className={"tk-chip" + (activo ? " activo" : "")}
                      onClick={() => set({ por: activo ? por.filter((x) => x !== canal) : [...por, canal] })}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <Destinatarios
              a={paso.a} gente={ctx.gente} nombres={ctx.nombres} yo={ctx.yo}
              onCambio={(a) => set({ a })} deshabilitado={ro}
            />
          </>
        )}
        {(paso.tipo === "whatsapp" || paso.tipo === "mail") && (
          <>
            <Destinatarios
              a={paso.a} gente={ctx.gente} nombres={ctx.nombres} yo={ctx.yo}
              onCambio={(a) => set({ a })} deshabilitado={ro}
            />
            {paso.tipo === "mail" && (
              <Campo label="Asunto"><input aria-label="Asunto" {...conVariables("asunto")} /></Campo>
            )}
            <Campo label="Texto"><textarea aria-label="Texto" rows={3} {...conVariables("texto")} /></Campo>
          </>
        )}
        {llevaChips && !ro && <Chips variables={variables} onElegir={insertar} deshabilitado={ro} />}
        <Errores lista={errores} />
      </div>
    </section>
  );
}

/* ── El bloque «Cuando…» ───────────────────────────────────────────────── */

function BloqueCuando({ d, onCambio, ro, ctx, errores }) {
  const set = (campos) => onCambio({ ...d, ...campos });
  const alternarDia = (n) => {
    const dias = d.dias || [];
    set({ dias: dias.includes(n) ? dias.filter((x) => x !== n) : [...dias, n].sort((a, b) => a - b) });
  };
  return (
    <section className="auto-bloque auto-bloque--cuando" aria-label="Cuando">
      <div className="auto-bloque-cab">
        <span className="auto-bloque-verbo">Cuando…</span>
        <div className="auto-tipos" role="group" aria-label="Qué la dispara">
          {DISPARADORES.map((t) => (
            <button
              key={t.tipo} type="button" disabled={ro} title={t.sub} aria-pressed={d.tipo === t.tipo}
              className={"tk-chip" + (d.tipo === t.tipo ? " activo" : "")}
              onClick={() => onCambio({ ...disparadorVacio(t.tipo), ...d, tipo: t.tipo })}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="auto-bloque-cuerpo">
        {d.tipo === "horario" && (
          <>
            <div className="auto-campo">
              <span className="auto-campo-label">Qué días</span>
              <div className="auto-dias" role="group" aria-label="Días">
                {DIAS.map((n, i) => (
                  <button
                    key={n} type="button" disabled={ro} aria-pressed={(d.dias || []).includes(i)}
                    className={"tk-chip" + ((d.dias || []).includes(i) ? " activo" : "")}
                    onClick={() => alternarDia(i)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <Campo label="A qué hora" ayuda="Hora de Argentina. Corre una vez por día elegido.">
              <input type="time" aria-label="Hora" value={d.hora || ""} disabled={ro} onChange={(e) => set({ hora: e.target.value })} />
            </Campo>
          </>
        )}
        {d.tipo === "mail" && (
          <>
            <p className="auto-nota" role="note">
              El bot lee los mails de la etiqueta <b>Automatizaciones</b> de <b>cepi@fibot.ar</b>:
              reenviá o filtrá ahí los mails.
            </p>
            <div className="auto-fila">
              <Campo label="Del remitente contiene" ayuda="Opcional. Sin mayúsculas: «ypf» encuentra «avisos@YPF.com».">
                <input value={d.remitente || ""} disabled={ro} placeholder="ypf.com" onChange={(e) => set({ remitente: e.target.value })} />
              </Campo>
              <Campo label="Asunto contiene" ayuda="Opcional. Sin los dos, corre con cada mail de la etiqueta.">
                <input value={d.asunto || ""} disabled={ro} placeholder="cambio de precios" onChange={(e) => set({ asunto: e.target.value })} />
              </Campo>
            </div>
          </>
        )}
        {d.tipo === "planilla_cambia" && (
          <>
            <ElegirPlanilla
              etiqueta="Cuál de tus planillas" planillaId={d.planilla_id} hoja={d.hoja}
              opciones={ro ? ctx.planillas : ctx.planillasMias} hojas={ctx.hojas} cargarHojas={ctx.cargarHojas}
              onCambio={set} deshabilitado={ro}
            />
            <Campo label="Qué rango" ayuda="Como en la planilla: B2:D10, o una celda sola como B3. Compara los valores calculados, con las fórmulas resueltas.">
              <input value={d.rango || ""} disabled={ro} placeholder="B2:D10" onChange={(e) => set({ rango: e.target.value })} />
            </Campo>
          </>
        )}
        <p className="auto-vars tenue">
          Deja para usar: {(VARIABLES_DISPARADOR[d.tipo] || []).map((v) => `{{${v}}}`).join(" ")}
        </p>
        <Errores lista={errores} />
      </div>
    </section>
  );
}

/* ── La prueba ─────────────────────────────────────────────────────────── */

function DetalleCorrida({ corrida, nombres }) {
  const secciones = detalleLegible(corrida.detalle, { prueba: corrida.tipo === "prueba", gente: nombres });
  return (
    <div className="auto-detalle">
      {secciones.map((s, i) => (
        <div key={i} className="auto-detalle-sec">
          {s.titulo && <div className="auto-detalle-tit">{s.titulo}</div>}
          <ul>{s.lineas.map((l, j) => <li key={j}>{l}</li>)}</ul>
        </div>
      ))}
      {corrida.error && <p className="auto-error-txt"><b>Error:</b> {corrida.error}</p>}
      {!secciones.length && !corrida.error && <p className="tenue">El bot no dejó detalle.</p>}
    </div>
  );
}

function Prueba({ id, yo, bloqueada, nombres, alTerminar }) {
  const [p, setP] = useState(null); // { fase, corrida, tarde, error }
  const dejar = useRef(null);
  const reloj = useRef(null);
  const limpiar = () => {
    if (dejar.current) { dejar.current(); dejar.current = null; }
    if (reloj.current) { clearTimeout(reloj.current); reloj.current = null; }
  };
  useEffect(() => limpiar, []);

  const probar = async () => {
    limpiar();
    setP({ fase: "pidiendo" });
    let corrida;
    try {
      corrida = await pedirPrueba(id, yo);
    } catch (e) {
      setP({ fase: "nopude", error: e && e.message });
      return;
    }
    setP({ fase: "esperando", corrida, tarde: false });
    dejar.current = seguirCorrida(corrida.id, (fila) => {
      if (TERMINADAS.includes(fila.estado)) {
        limpiar();
        setP({ fase: "listo", corrida: fila });
        alTerminar?.();
      } else {
        setP((x) => (x && x.fase === "esperando" ? { ...x, corrida: fila } : x));
      }
    });
    /* Dos minutos y nada: se dice. La corrida queda pedida y se sigue
       mirando —si el bot vuelve, el resultado aparece igual—, pero callarse
       sería dejar a alguien mirando «esperando…» sin saber que el bot no
       está. */
    reloj.current = setTimeout(() => {
      setP((x) => (x && x.fase === "esperando" ? { ...x, tarde: true } : x));
    }, ESPERA_BOT_MS);
  };

  const ocupado = p && (p.fase === "pidiendo" || p.fase === "esperando");
  return (
    <div className="auto-prueba">
      <button type="button" className="btn-suave" disabled={!!bloqueada || ocupado} onClick={probar} title={bloqueada || "El bot la corre sin escribir ni mandar nada"}>
        Probar
      </button>
      {bloqueada && <span className="tenue auto-prueba-motivo">{bloqueada}</span>}
      {p && (
        <div className="auto-prueba-res" role="status" aria-live="polite">
          {p.fase === "pidiendo" && <p className="tenue">Pidiendo la prueba…</p>}
          {p.fase === "nopude" && (
            <p className="auto-error-txt">No se pudo pedir la prueba{p.error ? `: ${p.error}` : "."}</p>
          )}
          {p.fase === "esperando" && (
            <>
              <p className="auto-esperando">
                {p.corrida && p.corrida.estado === "corriendo" ? "El bot la está corriendo…" : "Esperando al bot…"}
              </p>
              {p.tarde && (
                <p className="auto-nota">
                  {p.corrida && p.corrida.estado === "corriendo"
                    ? "El bot la tomó hace más de dos minutos y todavía no terminó. Puede estar trabado; si termina, el resultado aparece acá y en el historial."
                    : "Pasaron dos minutos y el bot no la tomó: puede estar apagado o trabado. La prueba queda pedida; si vuelve, la corre y aparece acá y en el historial."}
                </p>
              )}
            </>
          )}
          {p.fase === "listo" && (
            <>
              <p>
                Resultado de la prueba:{" "}
                <span className={`auto-estado auto-estado--${estadoCorrida(p.corrida.estado).tono}`}>{estadoCorrida(p.corrida.estado).label}</span>
              </p>
              <DetalleCorrida corrida={p.corrida} nombres={nombres} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── El historial ──────────────────────────────────────────────────────── */

/* `esMia`: el dueño lee la tabla entera; cualquier otro, la vista pública
   (cuándo y cómo le fue). Pedirle la tabla a un no-dueño devolvería cero
   filas y la pantalla diría «nunca corrió» de una que corre todos los días. */
function Historial({ id, version, nombres, esMia }) {
  const [estado, setEstado] = useState("cargando");
  const [corridas, setCorridas] = useState([]);
  const [abierta, setAbierta] = useState(null);
  useEffect(() => {
    let vivo = true;
    setEstado((e) => (e === "listo" ? e : "cargando"));
    (esMia ? corridasDe(id, 30) : corridasPublicasDe(id, 30))
      .then((cs) => { if (vivo) { setCorridas(cs); setEstado("listo"); } })
      .catch(() => vivo && setEstado("error"));
    return () => { vivo = false; };
  }, [id, version, esMia]);
  return (
    <BloqueFicha
      titulo="Últimas corridas"
      estado={estado}
      vacio={corridas.length === 0 ? "Todavía no corrió nunca, ni de prueba." : null}
    >
      {esMia ? (
        <TablaCorridas corridas={corridas} nombres={nombres} abierta={abierta} onAbrir={setAbierta} />
      ) : (
        <TablaCorridasAjena corridas={corridas} />
      )}
    </BloqueFicha>
  );
}

/* La de otro: cuándo, real o prueba, y cómo le fue. Lo demás no llega. */
function TablaCorridasAjena({ corridas }) {
  return (
    <>
      <div className="tabla-wrap">
        <table className="tabla auto-historial">
          <thead>
            <tr><th>Cuándo</th><th>Tipo</th><th>Estado</th></tr>
          </thead>
          <tbody>
            {corridas.map((c) => {
              const e = estadoCorrida(c.estado);
              return (
                <tr key={c.id}>
                  <td className="dato">{diaHoraAr(c.terminada_en || c.pedida_en)}</td>
                  <td>{c.tipo === "prueba" ? "Prueba" : "Real"}</td>
                  <td><span className={`auto-estado auto-estado--${e.tono}`}>{e.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function TablaCorridas({ corridas, nombres, abierta, onAbrir }) {
  return (
    <div className="tabla-wrap">
      <table className="tabla auto-historial">
        <thead>
          <tr><th>Cuándo</th><th>Tipo</th><th>Estado</th><th>Origen</th><th>Qué pasó</th></tr>
        </thead>
        <tbody>
          {corridas.map((c) => {
            const e = estadoCorrida(c.estado);
            const resumen = resumenDetalle(c.detalle, { prueba: c.tipo === "prueba", gente: nombres });
            const hayMas = c.detalle != null || c.error;
            return (
              <tr key={c.id}>
                <td className="dato">{diaHoraAr(c.terminada_en || c.pedida_en)}</td>
                <td>{c.tipo === "prueba" ? "Prueba" : "Real"}</td>
                <td><span className={`auto-estado auto-estado--${e.tono}`}>{e.label}</span></td>
                <td className="tenue">{c.origen || (c.tipo === "prueba" ? `pedida por ${nombres[(c.pedida_por || "").toLowerCase()] || c.pedida_por || "?"}` : "—")}</td>
                <td>
                  {c.error && <div className="auto-error-txt">{c.error}</div>}
                  {resumen && <div>{resumen}</div>}
                  {hayMas && (
                    abierta === c.id
                      ? <DetalleCorrida corrida={c} nombres={nombres} />
                      : <button type="button" className="rto-link" onClick={() => onAbrir(c.id)}>Ver todo</button>
                  )}
                  {!hayMas && <span className="tenue">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── La pantalla ───────────────────────────────────────────────────────── */

const firma = (form) => JSON.stringify(formAJson(form));

const Automatizacion = () => {
  const { id: idRuta } = useParams();
  const nueva = !idRuta || idRuta === "nueva";
  const navigate = useNavigate();

  const [estado, setEstado] = useState("cargando");
  const [fila, setFila] = useState(null);
  const [form, setForm] = useState(null);
  const [guardada, setGuardada] = useState(null); // la firma de lo guardado
  const [yo, setYo] = useState("");
  const [gente, setGente] = useState(null);
  const [planillas, setPlanillas] = useState(null);
  const [hojas, setHojas] = useState({});
  const [intento, setIntento] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [version, setVersion] = useState(0); // para recargar el historial

  useEffect(() => {
    let vivo = true;
    setEstado("cargando");
    setIntento(false);
    setAviso(null);
    const pedidos = [miMail(), nueva ? Promise.resolve(null) : traerAutomatizacion(idRuta)];
    Promise.all(pedidos)
      .then(([mail, f]) => {
        if (!vivo) return;
        setYo((mail || "").toLowerCase());
        const inicial = f ? jsonAForm(f) : formVacio();
        setFila(f);
        setForm(inicial);
        setGuardada(f ? firma(inicial) : null);
        setEstado("listo");
      })
      .catch(() => vivo && setEstado("error"));
    return () => { vivo = false; };
  }, [idRuta, nueva]);

  useEffect(() => {
    let vivo = true;
    genteParaCompartir().then((g) => vivo && setGente(g || [])).catch(() => {});
    misPlanillas().then((ps) => vivo && setPlanillas(ps || [])).catch(() => {});
    return () => { vivo = false; };
  }, []);

  const pedidas = useRef(new Set());
  const cargarHojas = useCallback((pid) => {
    if (!pid || pedidas.current.has(pid)) return;
    pedidas.current.add(pid);
    hojasDePlanilla(pid).then((ns) => setHojas((h) => ({ ...h, [pid]: ns }))).catch(() => {});
  }, []);

  const esMia = nueva || (!!fila && (fila.dueno || "").toLowerCase() === yo);
  const ro = !esMia;
  const nombres = useMemo(() => mapaDeGente(gente), [gente]);
  const ctx = useMemo(() => {
    const todas = planillas || [];
    return {
      yo,
      gente: gente || [],
      nombres,
      planillas: todas,
      planillasMias: todas.filter((p) => (p.dueno || "").toLowerCase() === yo),
      // Para mandar: todas, y las compartidas dicen de quién son, porque ésas
      // sólo se pueden mandar a uno mismo.
      planillasConDueno: todas.map((p) => {
        const dueno = (p.dueno || "").toLowerCase();
        return dueno && dueno !== yo ? { ...p, ajena: (gente || []).find((g) => (g.mail || "").toLowerCase() === dueno)?.nombre || dueno } : p;
      }),
      hojas,
      cargarHojas,
    };
  }, [planillas, gente, nombres, hojas, cargarHojas, yo]);

  const errores = useMemo(() => {
    if (!form || ro) return [];
    return validar(form, {
      planillasMias: planillas ? ctx.planillasMias.map((p) => p.id) : undefined,
      planillas: planillas ? planillas.map((p) => p.id) : undefined,
      hojas,
      gente: gente ? gente.map((g) => g.mail) : undefined,
    });
  }, [form, ro, planillas, ctx, hojas, gente]);

  if (estado === "cargando") return <div className="docs auto"><p className="tenue">Consultando…</p></div>;
  if (estado === "error" || !form) {
    return (
      <div className="docs auto">
        <Link to="/automatizaciones" className="rto-link">← Automatizaciones</Link>
        <p className="pf-nopude">No se pudo consultar.</p>
      </div>
    );
  }

  const pasos = form.pasos;
  const toma = pasos.filter((p) => grupoDe(p.tipo) === "toma");
  const hace = pasos.filter((p) => grupoDe(p.tipo) !== "toma");
  const sucio = nueva || firma(form) !== guardada;
  const verErrores = intento ? errores : [];
  const erroresDe = (donde) => verErrores.filter((e) => e.donde === donde);

  const cambiarPasos = (lista) => setForm((f) => ({ ...f, pasos: ordenarPasos(lista) }));
  const cambiarPaso = (nuevo) => cambiarPasos(pasos.map((p) => (p._k === nuevo._k ? nuevo : p)));
  const sacarPaso = (k) => cambiarPasos(pasos.filter((p) => p._k !== k));
  const sumarPaso = (tipo) => cambiarPasos([...pasos, pasoVacio(tipo)]);
  const moverPaso = (k, delta) => {
    const g = grupoDe(pasos.find((p) => p._k === k).tipo);
    const delGrupo = pasos.filter((p) => grupoDe(p.tipo) === g);
    const i = delGrupo.findIndex((p) => p._k === k);
    const j = i + delta;
    if (j < 0 || j >= delGrupo.length) return;
    const nuevo = [...delGrupo];
    [nuevo[i], nuevo[j]] = [nuevo[j], nuevo[i]];
    const otros = pasos.filter((p) => grupoDe(p.tipo) !== g);
    cambiarPasos(g === "toma" ? [...nuevo, ...otros] : [...otros, ...nuevo]);
  };

  async function guardar() {
    setIntento(true);
    setAviso(null);
    if (errores.length) return;
    setOcupado(true);
    try {
      const json = formAJson(form);
      if (nueva) {
        // Nace APAGADA: se prueba y después se prende (el contrato lo pide).
        const creada = await crearAutomatizacion({ ...json, activa: false });
        navigate(`/automatizaciones/${creada.id}`, { replace: true });
        return;
      }
      const f = await guardarAutomatizacion(fila.id, json);
      setFila(f);
      setGuardada(firma(form));
      setAviso({ tipo: "ok", texto: "Guardada." });
    } catch (e) {
      setAviso({ tipo: "error", texto: `No se pudo guardar${e && e.message ? `: ${e.message}` : "."}` });
    } finally {
      setOcupado(false);
    }
  }

  async function alternar() {
    setOcupado(true);
    setAviso(null);
    try {
      const f = await prenderApagar(fila.id, !fila.activa);
      setFila(f);
    } catch (e) {
      setAviso({ tipo: "error", texto: `No se pudo ${fila.activa ? "apagar" : "prender"}${e && e.message ? `: ${e.message}` : "."}` });
    } finally {
      setOcupado(false);
    }
  }

  async function borrar() {
    setOcupado(true);
    try {
      await borrarAutomatizacion(fila.id);
      navigate("/automatizaciones");
    } catch (e) {
      setOcupado(false);
      setConfirmarBorrar(false);
      setAviso({ tipo: "error", texto: `No se pudo borrar${e && e.message ? `: ${e.message}` : "."}` });
    }
  }

  const dueno = fila ? (fila.dueno || "").toLowerCase() : yo;
  const mailDisparador = form.disparador.tipo === "mail";
  const motivoPrueba = sucio ? "Guardá los cambios primero: se prueba lo guardado." : null;

  return (
    <div className="docs auto auto-editor">
      <Link to="/automatizaciones" className="rto-link">← Automatizaciones</Link>

      <header className="auto-cabeza">
        <input
          className="auto-nombre" aria-label="Nombre" placeholder="Ponele un nombre: «Precio del gasoil a mi planilla»"
          value={form.nombre} disabled={ro}
          onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
        />
        {fila && (
          <span className={"auto-onoff" + (fila.activa ? " auto-onoff--on" : "")}>{fila.activa ? "Prendida" : "Apagada"}</span>
        )}
      </header>
      <Errores lista={erroresDe("nombre")} />
      <p className="tenue auto-quien">
        {nueva ? "Nueva: se guarda apagada; la probás y después la prendés." : esMia ? "Es tuya." : `Es de ${nombres[dueno] || dueno}.`}
        {fila && fila.actualizado_en ? ` Última modificación: ${diaHoraAr(fila.actualizado_en)}.` : ""}
      </p>
      {ro && (
        <p className="auto-nota" role="note">
          La podés mirar, pero no cambiar.
        </p>
      )}
      <textarea
        className="auto-descripcion" rows={2} aria-label="Para qué sirve"
        placeholder={ro ? "" : "Para qué sirve (opcional)"} value={form.descripcion} disabled={ro}
        onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
      />

      <div className="auto-bloques">
        <BloqueCuando
          d={form.disparador} ro={ro} ctx={ctx} errores={erroresDe("disparador")}
          onCambio={(d) => setForm((f) => ({ ...f, disparador: d }))}
        />

        {toma.map((p, i) => (
          <BloquePaso
            key={p._k} paso={p} indice={i} total={toma.length} ro={ro} ctx={ctx}
            variables={variablesDisponibles(form.disparador, pasos, pasos.indexOf(p))}
            errores={erroresDe(`paso:${p._k}`)}
            onCambio={cambiarPaso} onMover={(d) => moverPaso(p._k, d)} onSacar={() => sacarPaso(p._k)}
          />
        ))}
        {!ro && (
          <div className="auto-sumar auto-sumar--toma">
            <span className="tenue">Tomá un dato (opcional):</span>
            {mailDisparador && (
              <button type="button" className="tk-chip" onClick={() => sumarPaso("leer_mail_ia")}>+ Leer el mail con IA</button>
            )}
            <button type="button" className="tk-chip" onClick={() => sumarPaso("leer_celda")}>+ Leer una celda</button>
          </div>
        )}

        {hace.map((p, i) => (
          <BloquePaso
            key={p._k} paso={p} indice={i} total={hace.length} ro={ro} ctx={ctx}
            variables={variablesDisponibles(form.disparador, pasos, pasos.indexOf(p))}
            errores={erroresDe(`paso:${p._k}`)}
            onCambio={cambiarPaso} onMover={(d) => moverPaso(p._k, d)} onSacar={() => sacarPaso(p._k)}
          />
        ))}
        {!ro && (
          <div className="auto-sumar auto-sumar--hace">
            <span className="tenue">Hacé algo:</span>
            <button type="button" className="tk-chip" onClick={() => sumarPaso("escribir_celda")}>+ Escribir en una celda</button>
            <button type="button" className="tk-chip" onClick={() => sumarPaso("whatsapp")}>+ WhatsApp</button>
            <button type="button" className="tk-chip" onClick={() => sumarPaso("mail")}>+ Mail</button>
            <button type="button" className="tk-chip" onClick={() => sumarPaso("mandar_planilla")}>+ Mandar una planilla</button>
          </div>
        )}
        <Errores lista={erroresDe("pasos")} />
      </div>

      {!ro && (
        <div className="auto-acciones">
          <button type="button" className="btn-primario" disabled={ocupado || (!sucio && !nueva)} onClick={guardar}>
            {nueva ? "Guardar (apagada)" : "Guardar"}
          </button>
          {fila && (
            <button
              type="button" className="btn-suave" disabled={ocupado || (sucio && !fila.activa)} onClick={alternar}
              title={sucio && !fila.activa ? "Guardá los cambios primero" : undefined}
            >
              {fila.activa ? "Apagar" : "Prender"}
            </button>
          )}
          {fila && !confirmarBorrar && (
            <button type="button" className="btn-suave auto-borrar" disabled={ocupado} onClick={() => setConfirmarBorrar(true)}>Borrar</button>
          )}
          {fila && confirmarBorrar && (
            <span className="auto-confirmar" role="group" aria-label="Confirmar borrado">
              ¿Borrarla? No se puede deshacer.
              <button type="button" className="btn-suave auto-borrar" disabled={ocupado} onClick={borrar}>Sí, borrar</button>
              <button type="button" className="btn-suave" onClick={() => setConfirmarBorrar(false)}>No</button>
            </span>
          )}
          {intento && errores.length > 0 && (
            <span className="auto-error-txt">Hay {errores.length === 1 ? "un detalle" : `${errores.length} detalles`} para corregir antes de guardar.</span>
          )}
          {aviso && <span className={aviso.tipo === "ok" ? "auto-ok-txt" : "auto-error-txt"} role="status">{aviso.texto}</span>}
        </div>
      )}

      {fila && esMia && (
        <Prueba
          id={fila.id} yo={yo} bloqueada={motivoPrueba} nombres={nombres}
          alTerminar={() => setVersion((v) => v + 1)}
        />
      )}

      {fila && <Historial id={fila.id} version={version} nombres={nombres} esMia={esMia} />}
    </div>
  );
};

export default Automatizacion;
